import { AxiosPromise } from "axios";
import { ValidationResult } from "formvalidation/hooks";
import React, { createContext, useEffect, useRef, useState } from "react";
import { InPageEditableSectionField } from "./types";
import equal from "fast-deep-equal";
import { IsInSidePanelContext, SidePanelActionsContext } from "components/SidePanel/SidePanelContext";

export interface InPageEditingState {
  fieldKey: string | undefined;
  value: any | undefined;
  initialValue: any | undefined;
  isUpdating: boolean;
  error: string | undefined;
  submitFunc: (value: any) => AxiosPromise<server.dto.ApiResponseWrapper<any> | server.dto.ApiResponse>;
}

const defaultInPageEditingState: InPageEditingState = {
  fieldKey: undefined,
  value: undefined,
  initialValue: undefined,
  isUpdating: false,
  error: undefined,
  submitFunc: () => undefined,
}

export interface InPageEditingContextValue {
  enabled: boolean;
  editingField: InPageEditingState;
  startInPageEdit: (field: InPageEditableSectionField) => void;
  updateValue: (field: InPageEditableSectionField, value: any) => void;
  saveInPageEdit: (field: InPageEditableSectionField, value: any) => void;
  clearInPageEdit: () => void;
  removeItem: (field: InPageEditableSectionField) => void;
}

const defaultInPageEditingContext = {
  enabled: false,
  editingField: defaultInPageEditingState,
  startInPageEdit: () => { },
  updateValue: () => { },
  saveInPageEdit: () => { },
  clearInPageEdit: () => { },
  removeItem: () => { },
}

export const InPageEditingContext = createContext<InPageEditingContextValue>(defaultInPageEditingContext);

const useInPageEditingState = () => {
  const [editingField, setEditingField] = useState<InPageEditingState>(defaultInPageEditingState);
  const [nextField, setNextField] = useState<InPageEditingState | undefined>(undefined);

  useEffect(() => {
    if (editingField.fieldKey === undefined && nextField !== undefined) {
      setEditingField(nextField);
      setNextField(undefined);
    }
  }, [editingField, nextField]);

  return [editingField, setEditingField, setNextField] as const;
}

interface InPageEditingProviderProps {
  enabled: boolean;
  validate: () => Promise<ValidationResult>;
  onValueChanged: (key: string, value: any) => (Promise<void> | void);
  onSuccessfulUpdate?: (key: string, newValue: any) => Promise<void>;
}

export const InPageEditingProvider = (props: React.PropsWithChildren<InPageEditingProviderProps>) => {
  const [editingField, setEditingField, setNextField] = useInPageEditingState();

  const { closePanel } = React.useContext(SidePanelActionsContext);
  const isInSidePanel = React.useContext(IsInSidePanelContext);

  // Mirrors editingField so that stale closures (e.g., a callback held by a detached
  // DOM node that outlived its section) can read the current state at call time.
  const stateRef = useRef(editingField);
  useEffect(() => { stateRef.current = editingField; }, [editingField]);

  const startInPageEdit = (field: InPageEditableSectionField) => {
    // Usually if we start editing, we want to close any open sidepanels.
    // Skip closing when:
    //  - hoverEdit fields can open a side panel for creation; a new panel will just stack on top
    //  - the edit itself is happening inside a side panel; closing would dismiss the panel the user is editing in
    if (!field.inPageEdit?.hoverEdit && !isInSidePanel) {
      closePanel();
    }

    if (editingField.fieldKey !== undefined && editingField.fieldKey !== field.key && !editingField.isUpdating) {
      clearInPageEdit();
    }
    setNextField(state => ({
      ...state,
      fieldKey: field.key,
      value: field.inPageEdit!.initialValue,
      initialValue: field.inPageEdit!.initialValue,
      submitFunc: field.inPageEdit!.submitChange,
    }));
  }
  const updateValue = (field: InPageEditableSectionField, value: any) => {
    if (stateRef.current.fieldKey !== field.key) return;
    setEditingField(state => ({ ...state, value }));
  }
  const clearInPageEdit = () => {
    setEditingField(defaultInPageEditingState)
  };

  const executeSubmit = async (fieldKey: string, value: any, initialValue: any, submitFunc: (v: any) => AxiosPromise<server.dto.ApiResponseWrapper<any> | server.dto.ApiResponse>): Promise<boolean> => {
    await props.onValueChanged(fieldKey, value);
    try {
      const response = await submitFunc(value);
      if (response.data.hasErrors) {
        setEditingField(state => ({
          ...state,
          value: initialValue,
          isUpdating: false,
          error: response.data.errors[0].message
        }));
        await props.onValueChanged(fieldKey, initialValue);
        return false;
      }
      if (props.onSuccessfulUpdate) {
        await props.onSuccessfulUpdate(fieldKey, value);
      }
    } catch (e: any) {
      console.error(e);
      setEditingField(state => ({
        ...state,
        value: initialValue,
        isUpdating: false,
        error: RESX.HttpError.Error500Title
      }));
      await props.onValueChanged(fieldKey, initialValue);
      return false;
    }
    return true;
  }

  const saveInPageEdit = async (field: InPageEditableSectionField, value: any) => {
    const s = stateRef.current;

    const isDetached = s.fieldKey !== field.key;
    if (!isDetached) {
      if (equal(value, s.initialValue)) {
        clearInPageEdit();
        return;
      }

      if (s.isUpdating) return;

      const validatorResult = await props.validate();
      if (validatorResult.hasErrors) return;

      setEditingField(state => ({ ...state, isUpdating: true }));
      const success = await executeSubmit(field.key, value, s.initialValue, s.submitFunc);
      if (success) clearInPageEdit();
      return;
    }

    // Detached path: the call came from a DOM node whose section is no longer the
    // currently-edited field (e.g., a side panel that's still rendered after the field is unmounted).
    // For example: A new contact was created in the sidepanel. Sometimes the edited state is lost, and then the save button is clicked in the new contact sidepanel
    if (s.isUpdating) {
      console.error(`[InPageEditing] Dropped detached save for '${field.key}'; another field ('${s.fieldKey}') is currently saving`);
      return;
    }
    if (equal(value, field.inPageEdit!.initialValue)) return;

    const validatorResult = await props.validate();
    if (validatorResult.hasErrors) {
      console.error(`[InPageEditing] Detached save for '${field.key}' failed validation`, validatorResult);
      return;
    }

    setEditingField({
      fieldKey: field.key,
      value,
      initialValue: field.inPageEdit!.initialValue,
      isUpdating: true,
      error: undefined,
      submitFunc: field.inPageEdit!.submitChange,
    });
    const success = await executeSubmit(field.key, value, field.inPageEdit!.initialValue, field.inPageEdit!.submitChange);
    if (!success) {
      console.error(`[InPageEditing] Detached save for '${field.key}' failed; field UI is gone so no error banner will render`);
    }
    if (success) clearInPageEdit();
  }

  const removeItem = async (field: InPageEditableSectionField) => {
    const s = stateRef.current;
    if (s.isUpdating) return;

    if (s.fieldKey !== undefined && s.fieldKey !== field.key) {
      console.error(`[InPageEditing] removeItem called for '${field.key}' while editing '${s.fieldKey}'; cancelling that edit to proceed`);
    }

    if (s.fieldKey !== undefined) {
      clearInPageEdit();
    }

    const validatorResult = await props.validate();
    if (validatorResult.hasErrors) {
      return;
    }

    setEditingField({
      fieldKey: field.key,
      value: undefined,
      initialValue: field.inPageEdit!.initialValue,
      isUpdating: true,
      error: undefined,
      submitFunc: field.inPageEdit!.submitChange,
    });

    const success = await executeSubmit(field.key, undefined, field.inPageEdit!.initialValue, field.inPageEdit!.submitChange);
    if (success) clearInPageEdit();
  }

  return <InPageEditingContext.Provider value={{
    enabled: props.enabled,
    editingField,
    startInPageEdit,
    updateValue,
    saveInPageEdit,
    clearInPageEdit,
    removeItem,
  }}>
    {props.children}
  </InPageEditingContext.Provider>
}